import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { CheckCircle, AlertCircle, ArrowLeft } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const [passwordRequirements, setPasswordRequirements] = useState({
    length: false,
    lowercase: false,
    uppercase: false,
    number: false,
    special: false
  });

  // Validate password requirements in real-time
  useEffect(() => {
    setPasswordRequirements({
      length: password.length >= 12,
      lowercase: /[a-z]/.test(password),
      uppercase: /[A-Z]/.test(password),
      number: /\d/.test(password),
      special: /[@$!%*?&]/.test(password)
    });
  }, [password]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Validate all password requirements
    if (!passwordRequirements.length) {
      setError('Password must be at least 12 characters long');
      return;
    }
    if (!passwordRequirements.lowercase) {
      setError('Password must contain at least one lowercase letter');
      return;
    }
    if (!passwordRequirements.uppercase) {
      setError('Password must contain at least one uppercase letter');
      return;
    }
    if (!passwordRequirements.number) {
      setError('Password must contain at least one number');
      return;
    }
    if (!passwordRequirements.special) {
      setError('Password must contain at least one special character (@$!%*?&)');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: password
      });
      setSuccess(true);
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'An error occurred. The reset link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 py-12 bg-slate-50">
        <div className="text-center">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>Invalid or missing reset token</AlertDescription>
          </Alert>
          <Link to="/login" className="mt-4 inline-block">
            <Button className="btn-primary">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to login
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-12 bg-slate-50">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_virtualcareclinic/artifacts/5rgbdbiq_PILGRIM%20%281%29.png" 
            alt="Pilgrim Health" 
            className="h-12 mx-auto mb-6"
          />
          <h2 className="text-3xl font-bold text-slate-900 mb-2">Reset your password</h2>
          <p className="text-slate-600">Enter your new password below</p>
        </div>

        <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-8">
          {success ? (
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-2">Password reset successful</h3>
              <p className="text-slate-600 mb-6">
                Your password has been successfully reset. Redirecting to login...
              </p>
            </div>
          ) : (
            <>
              {error && (
                <Alert variant="destructive" className="mb-6" data-testid="reset-password-error">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <form onSubmit={handleSubmit} className="space-y-6" data-testid="reset-password-form">
                <div>
                  <Label htmlFor="password">New password</Label>
                  <Input
                    id="password"
                    type="password"
                    data-testid="password-input"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    className="mt-1"
                  />
                  {/* Password Requirements Checklist */}
                  <div className="mt-3 space-y-2 p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <p className="text-xs font-semibold text-slate-700 mb-2">Password must contain:</p>
                    <div className="space-y-1.5">
                      <div className="flex items-center gap-2 text-xs">
                        {passwordRequirements.length ? (
                          <CheckCircle size={14} className="text-green-600 flex-shrink-0" />
                        ) : (
                          <div className="w-3.5 h-3.5 rounded-full border-2 border-slate-300 flex-shrink-0" />
                        )}
                        <span className={passwordRequirements.length ? "text-green-700 font-medium" : "text-slate-600"}>
                          At least 12 characters
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-xs">
                        {passwordRequirements.lowercase ? (
                          <CheckCircle size={14} className="text-green-600 flex-shrink-0" />
                        ) : (
                          <div className="w-3.5 h-3.5 rounded-full border-2 border-slate-300 flex-shrink-0" />
                        )}
                        <span className={passwordRequirements.lowercase ? "text-green-700 font-medium" : "text-slate-600"}>
                          One lowercase letter (a-z)
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-xs">
                        {passwordRequirements.uppercase ? (
                          <CheckCircle size={14} className="text-green-600 flex-shrink-0" />
                        ) : (
                          <div className="w-3.5 h-3.5 rounded-full border-2 border-slate-300 flex-shrink-0" />
                        )}
                        <span className={passwordRequirements.uppercase ? "text-green-700 font-medium" : "text-slate-600"}>
                          One uppercase letter (A-Z)
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-xs">
                        {passwordRequirements.number ? (
                          <CheckCircle size={14} className="text-green-600 flex-shrink-0" />
                        ) : (
                          <div className="w-3.5 h-3.5 rounded-full border-2 border-slate-300 flex-shrink-0" />
                        )}
                        <span className={passwordRequirements.number ? "text-green-700 font-medium" : "text-slate-600"}>
                          One number (0-9)
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-xs">
                        {passwordRequirements.special ? (
                          <CheckCircle size={14} className="text-green-600 flex-shrink-0" />
                        ) : (
                          <div className="w-3.5 h-3.5 rounded-full border-2 border-slate-300 flex-shrink-0" />
                        )}
                        <span className={passwordRequirements.special ? "text-green-700 font-medium" : "text-slate-600"}>
                          One special character (@$!%*?&)
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <Label htmlFor="confirmPassword">Confirm new password</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    data-testid="confirm-password-input"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    className="mt-1"
                  />
                </div>

                <Button
                  type="submit"
                  data-testid="reset-password-submit-button"
                  disabled={loading}
                  className="w-full btn-primary"
                >
                  {loading ? 'Resetting...' : 'Reset password'}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;